import { FC, PropsWithChildren, ReactNode, useContext, useEffect, useMemo, useRef } from 'react';
import { DpCatalogEntryCarouselHeader } from '@/components/blocks/DpLibrary/DpCatalogEntryCarouselHeader';
import { DpIARecommendationHeader } from '@/components/content/CatalogEntryIARecommendation/parts/DpIARecommendationHeader';
import { NullElement } from '@/components/blocks/NullElement';
import { CatalogEntryIARecommendationSkeleton } from '@/components/content/CatalogEntryIARecommendation/parts/CatalogEntryIARecommendationSkeleton';
// eslint-disable-next-line no-restricted-imports
import { DpIARecommendationResponse } from 'integration/generated/transactions/data-contracts';
import { ProductType } from '@/data/types/Product';
import { CLICK_PRODUCT_VIEW_SOURCE } from '@/data/constants/clickProductView';
import { useCatalogEntryIARecommendation } from '@/data/Content/CatalogEntryIARecommendation';
import { useSettings } from '@/data/Settings';
import { EventsContext } from '@/data/context/events';
import { useCatalogEntryIARecommendationItems } from '@/data/Content/CatalogEntryIARecommendationItems';
import { ContentProvider } from '@/data/context/content';
import { CatalogEntryCarousel } from '@/components/content/CatalogEntryRecommendation/parts/CatalogEntryCarousel';
import { usePDPContext } from '@/data/context/PDP';
import { catalogEntryOrderIaRecommendation } from '@/utils/catalogEntryOrder';
import { useRepurchasePricingEnabled } from '@/data/context/RepurchasePricing';
import { RepurchasePricingProvider } from '@/components/blocks/DpRepurchaseCoupon/parts/RepurchasePricingProvider';
import type {
	DpIARecommendationSizeMode,
	DpIARecommendationSizes,
	DpIARecommendationType,
	IARecommendationSource,
} from '@/data/Content/CatalogEntryIARecommendation';

const CarouselHeader: FC<{
	header?: ReactNode;
	title?: string;
	showRecommendationBadge: boolean;
}> = ({ header, title, showRecommendationBadge }) => {
	if (header) {
		return <>{header}</>;
	}
	if (!title) {
		return null;
	}
	return showRecommendationBadge ? (
		<DpIARecommendationHeader title={title} showRecommendationBadge />
	) : (
		<DpCatalogEntryCarouselHeader title={title} />
	);
};

const WithRepurchasePricing: FC<
	PropsWithChildren<{ products: ProductType[]; excludedPartNumber?: string }>
> = ({ products, excludedPartNumber, children }) =>
	useRepurchasePricingEnabled() ? (
		<RepurchasePricingProvider products={products} excludedPartNumber={excludedPartNumber}>
			{children}
		</RepurchasePricingProvider>
	) : (
		<>{children}</>
	);

export const CatalogEntryIARecommendation: FC<{
	id?: string;
	title?: string;
	params: {
		partNumber?: string;
	};
	transform?: {
		order: string;
	} | null;
	loadingElement?: JSX.Element;
	emptyElement?: JSX.Element;
	partNumbersExtractor: (data: DpIARecommendationResponse) => string[];
	emptyExtractor: (products: ProductType[]) => boolean;
	highlightFirstProduct?: boolean;
	showRecommendationBadge?: boolean;
	clickSource?: CLICK_PRODUCT_VIEW_SOURCE;
	filters?: Array<any>;
	types?: DpIARecommendationType[];
	sizes?: DpIARecommendationSizes;
	sizeMode?: DpIARecommendationSizeMode;
	header?: ReactNode;
	highlightLabel?: string;
	productsSelector?: (products: ProductType[], data: DpIARecommendationResponse) => ProductType[];
	source?: IARecommendationSource;
	seedPartNumbers?: string[];
	withoutIdentity?: boolean;
}> = ({
	params,
	transform,
	title,
	id = 'catalogEntryRecommendationIA',
	loadingElement,
	emptyElement,
	emptyExtractor,
	partNumbersExtractor,
	highlightFirstProduct = false,
	showRecommendationBadge = false,
	clickSource,
	filters,
	types,
	sizes,
	sizeMode,
	header,
	highlightLabel,
	productsSelector,
	source,
	seedPartNumbers,
	withoutIdentity,
}) => {
	const order = transform?.order;
	const orderExtractor = useMemo(
		() =>
			order || productsSelector
				? (prods: ProductType[], dataIA: DpIARecommendationResponse) => {
						const selected = productsSelector ? productsSelector(prods, dataIA) : prods;
						return order ? catalogEntryOrderIaRecommendation(order, selected, dataIA) : selected;
				  }
				: undefined,
		[order, productsSelector]
	);
	const { products, contract, isLoadingDpIARecommendation, isLoadingProducts } =
		useCatalogEntryIARecommendation({
			params,
			partNumbersExtractor,
			filters,
			types,
			sizes,
			sizeMode,
			orderExtractor,
			source,
			seedPartNumbers,
			withoutIdentity,
		});

	const { onCarouselItemListView } = useContext(EventsContext);
	const { settings } = useSettings();
	const repurchasePricingEnabled = useRepurchasePricingEnabled();

	const { slides, onNotify } = useCatalogEntryIARecommendationItems({
		products,
		isLoadingProducts,
		parentPartNumber: params?.partNumber,
		variant: repurchasePricingEnabled ? 'largeRepurchase' : 'large',
		clickSource,
		contract,
		params,
		transforms: {
			includePartNumber: highlightFirstProduct,
			highlightedPartNumber: highlightFirstProduct ? params?.partNumber : undefined,
			highlightedLabel: highlightLabel,
		},
	});

	const { product: parentProduct } = usePDPContext();

	const trackedKeyRef = useRef<string | null>(null);
	useEffect(() => {
		if (isLoadingProducts || !products?.length || !settings) return;

		const item_list_id = clickSource ?? id;
		const key = `${item_list_id}|${products.map((p) => p.partNumber).join(',')}`;
		if (trackedKeyRef.current === key) return;
		trackedKeyRef.current = key;

		onCarouselItemListView({
			gtm: {
				parentPartNumber: params?.partNumber,
				parentProduct,
				products,
				item_list_id,
				settings,
			},
		});
	}, [
		parentProduct,
		products,
		isLoadingProducts,
		params?.partNumber,
		clickSource,
		id,
		title,
		settings,
		onCarouselItemListView,
	]);

	if (isLoadingDpIARecommendation) {
		return loadingElement || <CatalogEntryIARecommendationSkeleton id={id} title={title} />;
	}

	if (!isLoadingProducts && emptyExtractor?.(products)) {
		return emptyElement || <NullElement />;
	}

	return (
		<>
			<CarouselHeader
				header={header}
				title={title}
				showRecommendationBadge={showRecommendationBadge}
			/>
			<WithRepurchasePricing
				products={products}
				excludedPartNumber={highlightFirstProduct ? params?.partNumber : undefined}
			>
				<ContentProvider value={{ onNotify }}>
					<CatalogEntryCarousel slides={slides} id={id.toString()} />
				</ContentProvider>
			</WithRepurchasePricing>
		</>
	);
};
